/**
 * Moving a sample into a location: the one move scanning and the sample forms (`web/providers/SampleProvider.js`
 * `recordStorageLocation`) both use, so a sample ends up in one place with a log entry however it was moved.
 */

const libSampleScanStatus = require('./Sample-Scan-Status.js');

/**
 * @typedef {Object} SampleMoveOptions
 * @property {boolean} [RemoveFromCurrentLocation] - Default true. False adds the location and keeps the others.
 * @property {import('./Sample-Scan-Status.js').ReceiveConfig|null} [ReceiveConfig] - Left out or null, the status isn't touched.
 * @property {Array<{ IDLocation: number }>} [CurrentLocations] - Where the sample is now, when the caller has already read it.
 * @property {{ CaptureLatitude?: number, CaptureLongitude?: number }|null} [Position]
 * @property {string|((pMove: { To: string, From: string }) => string)} [Notes] - The log entry: a fixed text, or one
 *     built from the location's name (To) and the names of the locations the sample was taken out of (From, '' when
 *     it wasn't in one). Default "Sample moved to <To>", or "Sample moved from <From> to <To>".
 * @property {Array<{ IDLocation: number, Name?: string }>} [Locations] - The customer's locations, when the caller has
 *     them, so the From names don't need reading.
 */

/**
 * @typedef {Object} SampleMoveResult
 * @property {Array<number>} PreviousIDLocations - The other locations it was in, newest first.
 * @property {string} PreviousStatus
 * @property {{ Status: string, Kept?: string }} StatusDecision - Status is the one set ('' for none).
 */

/**
 * Move a sample into a location: add it there, take it out of anywhere else, set any status the location
 * calls for, and log the move.
 *
 * @param {import('./Sample-Scan-Store.js').SampleScanStore} pStore
 * @param {{ IDSample: number, Status?: string }} pSample
 * @param {{ IDLocation: number, Name?: string, QRCode?: string, GUIDLocation?: string }} pLocation
 * @param {SampleMoveOptions} [pOptions]
 * @return {Promise<SampleMoveResult>}
 */
/**
 * @param {Array<string>} pNames
 * @return {string} "A", "A and B", or "A, B and C"
 */
function joinNames(pNames)
{
	if (pNames.length < 2)
	{
		return pNames[0] || '';
	}
	return `${pNames.slice(0, -1).join(', ')} and ${pNames[pNames.length - 1]}`;
}

/**
 * The names of the locations a sample is being taken out of, from the caller's list, else read through the store.
 * A name that can't be found is "location #<id>", so the log still says where the sample was.
 *
 * @param {import('./Sample-Scan-Store.js').SampleScanStore} pStore
 * @param {Array<number>} pIDLocations
 * @param {Array<{ IDLocation: number, Name?: string }>} [pLocations]
 * @return {Promise<Array<string>>}
 */
async function locationNames(pStore, pIDLocations, pLocations)
{
	const tmpNames = [];
	for (const tmpIDLocation of pIDLocations)
	{
		let tmpLocation = (pLocations || []).find((pLocation) => Number(pLocation.IDLocation) === Number(tmpIDLocation)) || null;
		if (!tmpLocation && (typeof pStore.getLocation === 'function'))
		{
			tmpLocation = await Promise.resolve().then(() => pStore.getLocation(tmpIDLocation)).catch(() => null);
		}
		tmpNames.push((tmpLocation && tmpLocation.Name) ? tmpLocation.Name : `location #${tmpIDLocation}`);
	}
	return tmpNames;
}

/**
 * @param {{ To: string, From: string }} pMove
 * @return {string} the default log entry
 */
function defaultNotes(pMove)
{
	return pMove.From ? `Sample moved from ${pMove.From} to ${pMove.To}` : `Sample moved to ${pMove.To}`;
}

async function moveSampleToLocation(pStore, pSample, pLocation, pOptions)
{
	const tmpOptions = pOptions || {};
	const tmpCurrent = tmpOptions.CurrentLocations || await pStore.getSampleLocations(pSample.IDSample);
	if (!Array.isArray(tmpCurrent))
	{
		throw new Error('could not read where the sample is now');
	}
	const tmpPrevious = [];
	for (const tmpJoin of tmpCurrent)
	{
		const tmpIDLocation = tmpJoin && tmpJoin.IDLocation;
		if (tmpIDLocation && (tmpIDLocation !== pLocation.IDLocation) && !tmpPrevious.includes(tmpIDLocation))
		{
			tmpPrevious.push(tmpIDLocation);
		}
	}
	const tmpPreviousStatus = pSample.Status || '';

	await pStore.addSampleToLocation(pSample.IDSample, pLocation.IDLocation);
	if (tmpOptions.RemoveFromCurrentLocation !== false)
	{
		for (const tmpIDLocation of tmpPrevious)
		{
			await pStore.removeSampleFromLocation(pSample.IDSample, tmpIDLocation);
		}
	}

	const tmpDecision = tmpOptions.ReceiveConfig ? libSampleScanStatus.getStatusForMove(tmpOptions.ReceiveConfig, pLocation, tmpPreviousStatus) : { Status: '' };
	if (tmpDecision.Status)
	{
		await pStore.setSampleStatus(pSample.IDSample, tmpDecision.Status);
	}

	// The log says where the sample came from: the other locations it was in (newest first), not this one.
	let tmpNotes = tmpOptions.Notes;
	if (typeof tmpNotes !== 'string')
	{
		const fNotes = (typeof tmpNotes === 'function') ? tmpNotes : defaultNotes;
		tmpNotes = fNotes({ To: String(pLocation.Name || pLocation.IDLocation), From: joinNames(await locationNames(pStore, tmpPrevious, tmpOptions.Locations)) });
	}

	const tmpPosition = tmpOptions.Position || {};
	await pStore.createSampleLog(
		{
			IDSample: pSample.IDSample,
			IDLocation: pLocation.IDLocation,
			CaptureLatitude: tmpPosition.CaptureLatitude,
			CaptureLongitude: tmpPosition.CaptureLongitude,
			FromStatus: tmpDecision.Status ? tmpPreviousStatus : undefined,
			ToStatus: tmpDecision.Status || undefined,
			Notes: tmpNotes,
		});

	return { PreviousIDLocations: tmpPrevious, PreviousStatus: tmpPreviousStatus, StatusDecision: tmpDecision };
}

module.exports =
{
	moveSampleToLocation,
	joinNames,
};
